import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/services/api";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, MoreHorizontal, UserCheck, Shield, Trash2, Search, ChevronLeft, ChevronRight, Users } from "lucide-react";
import PageHeader from "@/components/common/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import type { Database } from "@/integrations/supabase/types";

type AppRole = Database["public"]["Enums"]["app_role"];

interface UserWithRole {
  user_id: string;
  full_name: string;
  email: string;
  roles: AppRole[];
}

const PAGE_SIZE = 10;

const roleConfig: Record<AppRole, { label: string; className: string }> = {
  admin:      { label: "Admin",      className: "bg-red-100 text-red-700 ring-1 ring-red-200" },
  aca_member: { label: "ACA Member", className: "bg-blue-100 text-blue-700 ring-1 ring-blue-200" },
  growlearn:  { label: "GrowLearn",  className: "bg-indigo-100 text-indigo-700 ring-1 ring-indigo-200" },
  student:    { label: "Student",    className: "bg-muted text-muted-foreground ring-1 ring-border" },
};

const RoleBadge = ({ role }: { role: AppRole }) => {
  const cfg = roleConfig[role] || { label: role, className: "bg-muted text-muted-foreground ring-1 ring-border" };
  return (
    <span className={cn("inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold", cfg.className)}>
      {cfg.label}
    </span>
  );
};

const roleLabel = (role: AppRole) => roleConfig[role]?.label ?? role;

const ManageUsers = () => {
  const [users, setUsers] = useState<UserWithRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();

  const fetchUsers = async () => {
    setLoading(true);
    const { data: profiles } = await supabase.from("profiles").select("user_id, full_name, email");
    const { data: roles } = await supabase.from("user_roles").select("user_id, role");
    if (profiles && roles) {
      const roleMap = new Map<string, AppRole[]>();
      roles.forEach((r) => {
        const existing = roleMap.get(r.user_id) || [];
        existing.push(r.role);
        roleMap.set(r.user_id, existing);
      });
      setUsers(profiles.map((p) => ({ user_id: p.user_id, full_name: p.full_name, email: p.email, roles: roleMap.get(p.user_id) || ["student"] })));
    }
    setLoading(false);
  };

  useEffect(() => { fetchUsers(); }, []);
  useEffect(() => { setPage(1); }, [search]);

  const isCurrentUser = (userId: string) => currentUser?.id === userId;

  const filtered = users.filter(
    (u) =>
      u.full_name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase())
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const assignRole = async (userId: string, role: AppRole) => {
    const { error } = await supabase.from("user_roles").insert({ user_id: userId, role });
    if (error) {
      toast({ title: error.code === "23505" ? "Already assigned" : "Error", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Role assigned", description: `${roleLabel(role)} role assigned.` });
    fetchUsers();
  };

  const removeRole = async (userId: string, role: AppRole) => {
    if (role === "admin") { toast({ title: "Cannot remove", description: "Admin role cannot be removed by GrowLearn.", variant: "destructive" }); return; }
    const { error } = await supabase.from("user_roles").delete().eq("user_id", userId).eq("role", role);
    if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    const { data: remaining } = await supabase.from("user_roles").select("role").eq("user_id", userId);
    if (!remaining || remaining.length === 0) await supabase.from("user_roles").insert({ user_id: userId, role: "student" });
    toast({ title: "Role removed" });
    fetchUsers();
  };

  const demoteToStudent = async (userId: string) => {
    if (isCurrentUser(userId)) { toast({ title: "Cannot demote yourself", variant: "destructive" }); return; }
    await supabase.from("user_roles").delete().eq("user_id", userId).neq("role", "student").neq("role", "admin");
    const { data: hasStudent } = await supabase.from("user_roles").select("id").eq("user_id", userId).eq("role", "student").maybeSingle();
    if (!hasStudent) await supabase.from("user_roles").insert({ user_id: userId, role: "student" });
    toast({ title: "User demoted to student" });
    fetchUsers();
  };

  const deleteUser = async (userId: string) => {
    setDeletingId(userId);
    try {
      await api.delete(`/users/${userId}`);
      toast({ title: "User deleted" });
      fetchUsers();
    } catch (e: any) {
      toast({ title: "Delete failed", description: e.response?.data?.error || e.message, variant: "destructive" });
    }
    setDeletingId(null);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <PageHeader
          title="Manage Users"
          actions={
            <Button
              onClick={() => navigate("/growlearn/users/new")}
              className="rounded-full gradient-warm text-white border-0 shadow-lg hover:opacity-90 transition-opacity"
            >
              <Plus className="mr-2 h-4 w-4" /> Create User
            </Button>
          }
        />

        {/* Search */}
        <div className="relative max-w-sm ml-auto">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-9 bg-card/80 backdrop-blur-sm"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          )}
        </div>

        {/* Table */}
        <Card className="glass-premium shadow-lg overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      <TableHead className="w-10 text-center">#</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Roles</TableHead>
                      <TableHead className="w-[60px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginated.map((u, idx) => (
                      <TableRow key={u.user_id} className="hover:bg-accent/30 transition-colors">
                        <TableCell className="text-center text-xs text-muted-foreground w-10">
                          {(page - 1) * PAGE_SIZE + idx + 1}
                        </TableCell>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full gradient-warm flex items-center justify-center text-xs font-bold text-white shrink-0">
                              {u.full_name ? u.full_name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) : "?"}
                            </div>
                            <span>{u.full_name || "—"}</span>
                            {isCurrentUser(u.user_id) && (
                              <Badge variant="outline" className="text-xs">You</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{u.email}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {u.roles.map((r) => <RoleBadge key={r} role={r} />)}
                          </div>
                        </TableCell>
                        <TableCell>
                          {!isCurrentUser(u.user_id) && !u.roles.includes("admin") && (
                            <div className="flex items-center gap-2">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    title="Manage roles"
                                    className="h-8 w-8 p-0 border-border/60 hover:bg-primary hover:text-white hover:border-primary transition-all duration-200"
                                  >
                                    <MoreHorizontal className="h-3.5 w-3.5" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {!u.roles.includes("growlearn") && <DropdownMenuItem onClick={() => assignRole(u.user_id, "growlearn")}><UserCheck className="mr-2 h-4 w-4" />Assign GrowLearn</DropdownMenuItem>}
                                  {!u.roles.includes("aca_member") && <DropdownMenuItem onClick={() => assignRole(u.user_id, "aca_member")}><Shield className="mr-2 h-4 w-4" />Assign ACA Member</DropdownMenuItem>}
                                  {u.roles.includes("growlearn") && <DropdownMenuItem onClick={() => removeRole(u.user_id, "growlearn")} className="text-destructive">Remove GrowLearn</DropdownMenuItem>}
                                  {u.roles.includes("aca_member") && <DropdownMenuItem onClick={() => removeRole(u.user_id, "aca_member")} className="text-destructive">Remove ACA Member</DropdownMenuItem>}
                                  {(u.roles.includes("growlearn") || u.roles.includes("aca_member")) && (
                                    <DropdownMenuItem onClick={() => demoteToStudent(u.user_id)} className="text-destructive"><Trash2 className="mr-2 h-4 w-4" />Demote to Student</DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>

                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    title="Delete user"
                                    disabled={deletingId === u.user_id}
                                    className="h-8 w-8 p-0 border-border/60 text-red-600 hover:bg-red-500 hover:text-white hover:border-red-500 transition-all duration-200"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete user?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will permanently delete <strong>{u.full_name || u.email}</strong> ({u.email}) and all their data. This cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      className="bg-red-500 hover:bg-red-600 text-white"
                                      onClick={() => deleteUser(u.user_id)}
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {filtered.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="py-12 text-center text-muted-foreground">
                          <Users className="mx-auto h-8 w-8 text-muted-foreground/30 mb-2" />
                          No users found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
            {!loading && filtered.length > 0 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border/50">
                <p className="text-xs text-muted-foreground">
                  Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} users
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="h-8 w-8 flex items-center justify-center rounded-md text-xs font-semibold gradient-warm text-white">
                    {page}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ManageUsers;
